import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { field, input, textLabel, focusIndicator, icon } from './styles.js';
import { usePrevMounted } from '../utils/hooks/usePrevMounted.js';
import { useDisabledCallback } from '../utils/hooks/useDisabledCallback.js';
import { ThemeProvider } from 'styled-components';
import { useForkRef } from '../utils/hooks/useForkRef.js';
import { useSmartControlledToggle } from '../utils/hooks/useSmartControlledToggle.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { useComponentVariant } from '../utils/hooks/useComponentVariant.js';

/**
 * Checkboxes are used when there is a list of options and the user may select
 * any number of choices (including zero).
 */
var Checkbox = function Checkbox(_ref) {
  var color = _ref.color,
      inactiveColor = _ref.inactiveColor,
      invert = _ref.invert,
      label = _ref.label,
      onChange = _ref.onChange,
      readOnly = _ref.readOnly,
      checkedProp = _ref.checked,
      id = _ref.id,
      name = _ref.name,
      value = _ref.value,
      disabled = _ref.disabled,
      inputProps = _ref.inputProps,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      inputRefProp = _ref.inputRef,
      variantProp = _ref.variant,
      props = _objectWithoutProperties(_ref, ["color", "inactiveColor", "invert", "label", "onChange", "readOnly", "checked", "id", "name", "value", "disabled", "inputProps", "onBlur", "onFocus", "inputRef", "variant"]);

  var _usePrevMounted = usePrevMounted(),
      _usePrevMounted2 = _slicedToArray(_usePrevMounted, 1),
      prevMounted = _usePrevMounted2[0];

  var internalInputRef = React.useRef(null); // istanbul ignore next -- implementation detail

  var inputRef = inputRefProp ? useForkRef(inputRefProp, internalInputRef) : internalInputRef;

  var _useSmartControlledTo = useSmartControlledToggle(false, checkedProp, onChange),
      _useSmartControlledTo2 = _slicedToArray(_useSmartControlledTo, 2),
      checked = _useSmartControlledTo2[0],
      handleInputChange = _useSmartControlledTo2[1];

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      focused = _React$useState2[0],
      setFocused = _React$useState2[1];

  React.useEffect(function () {
    var _internalInputRef$cur, _internalInputRef$cur2;

    // if element is disabled, React onBlur is not called!
    // this leaves focus indication on even if focus is removed by browser
    // using the native addEventListener works
    // this problem proly exists in other focus indicated elements as well - tested here first for possible bugs
    // https://github.com/facebook/react/issues/9142
    var blurHandler = function blurHandler(event) {
      setFocused(false);
      onBlur && onBlur(event);
    };

    var focusHandler = function focusHandler(event) {
      setFocused(true);
      onFocus && onFocus(event);
    };

    internalInputRef === null || internalInputRef === void 0 ? void 0 : (_internalInputRef$cur = internalInputRef.current) === null || _internalInputRef$cur === void 0 ? void 0 : _internalInputRef$cur.addEventListener('blur', blurHandler);
    internalInputRef === null || internalInputRef === void 0 ? void 0 : (_internalInputRef$cur2 = internalInputRef.current) === null || _internalInputRef$cur2 === void 0 ? void 0 : _internalInputRef$cur2.addEventListener('focus', focusHandler);
    return function () {
      var _internalInputRef$cur3, _internalInputRef$cur4;

      internalInputRef === null || internalInputRef === void 0 ? void 0 : (_internalInputRef$cur3 = internalInputRef.current) === null || _internalInputRef$cur3 === void 0 ? void 0 : _internalInputRef$cur3.removeEventListener('blur', blurHandler);
      internalInputRef === null || internalInputRef === void 0 ? void 0 : (_internalInputRef$cur4 = internalInputRef.current) === null || _internalInputRef$cur4 === void 0 ? void 0 : _internalInputRef$cur4.removeEventListener('focus', focusHandler);
    };
  }, [internalInputRef, onBlur, onFocus]); // Misuse React.useMemo a little to only animate once checked state changes

  var animate = React.useMemo(function () {
    return prevMounted;
  }, [checked]);
  var disabledHandleInputChange = useDisabledCallback(handleInputChange, Boolean(readOnly || disabled));
  var theme = useTheme();
  var variant = variantProp || theme.variant;
  var rawTheme = theme.checkbox.web;
  var checkboxThemeWeb = useComponentVariant(rawTheme, variant); // https://bugs.webkit.org/show_bug.cgi?id=191743
  // there seems to be a bug where safari + voice over reads labels twice
  // removing htmlFor make NVDA not to read any label

  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(field, _extends({
    "data-test": "elemental-Checkbox-".concat(id || name, "-").concat(value)
  }, props), /*#__PURE__*/React.createElement(input, _extends({
    "data-test": "elemental-Checkbox-".concat(id || name, "-").concat(value, "-input"),
    invert: invert,
    onChange: disabledHandleInputChange,
    type: "checkbox",
    id: id || "elemental-Checkbox-".concat(name, "-").concat(value, "__label"),
    name: name,
    value: value,
    readOnly: readOnly,
    disabled: disabled || readOnly,
    disabledStyle: disabled,
    checked: checked,
    color: color ? color : checkboxThemeWeb.color,
    ref: inputRef
  }, inputProps)), /*#__PURE__*/React.createElement(textLabel, {
    color: color ? color : checkboxThemeWeb.color,
    inactiveColor: inactiveColor ? inactiveColor : checkboxThemeWeb.unselected.color,
    readOnly: readOnly,
    htmlFor: id || "elemental-Checkbox-".concat(name, "-").concat(value, "__label"),
    variant: variant
  }, label), focused && /*#__PURE__*/React.createElement(focusIndicator, {
    color: color ? color : checkboxThemeWeb.color
  }), /*#__PURE__*/React.createElement(icon, {
    animate: animate,
    invert: invert,
    readOnly: readOnly,
    checked: checked,
    color: color ? color : checkboxThemeWeb.color,
    "data-test": "elemental-Checkbox-".concat(id || name, "-").concat(value, "-icon")
  })));
};

export { Checkbox };
